import { MappedWardobeField } from '@hooks/jobsBoard/useApplyToApplicationJob';
import { MinMaxValues, MinMaxValuesUK } from '@lib/utils/constants';
import {
  ONLY_DIGITS_REGEX,
  US_COAT_LENGTH_OPTIONS,
  US_COAT_LENGTH_VALUE_TO_OPTION,
  WHOLE_NUMBER_ERROR_MESSAGE,
} from '@lib/utils/constants/measurementConstants';
import { StyledGapContainer } from '@views/auth/signup/styles';
import { StyledInput } from '@views/jobsBoard/JobsBoardSection/JobsBoardFilters/JobsBoardFiltersModal/styles';

import { StyledFormItem, StyledLabel, StyledSelect } from '../styles';
type WardrobeQuestionProps = {
  wardrobeItem: MappedWardobeField;
  userAppearance: UserAppearance;
  projectLocationCountry: ProductionCountry;
};

export const JobsBoardWardrobeQuestion = ({
  wardrobeItem,
  userAppearance,
  projectLocationCountry,
}: WardrobeQuestionProps) => {
  const { label, options, profileField } = wardrobeItem;
  const minMaxValuesMapper =
    projectLocationCountry === 'UK' ? MinMaxValuesUK : MinMaxValues;

  return (
    <StyledGapContainer gap={4}>
      <StyledLabel label={label} requiredSymbol />

      <StyledFormItem
        name={profileField}
        initialValue={userAppearance?.[profileField]}
        rules={[
          {
            validator: (_, value) => {
              if (!options) {
                // this is free text

                if (!value) {
                  return Promise.reject('This field is required');
                }

                if (!ONLY_DIGITS_REGEX.test(value)) {
                  return Promise.reject(WHOLE_NUMBER_ERROR_MESSAGE);
                }

                const intValue = parseInt(value);
                if (!Number.isInteger(intValue)) {
                  return Promise.reject(WHOLE_NUMBER_ERROR_MESSAGE);
                }

                const maxRange = minMaxValuesMapper?.[profileField]?.max;
                const minRange = minMaxValuesMapper?.[profileField]?.min;

                if (Number.isFinite(maxRange) && Number.isFinite(minRange)) {
                  // we validate the range
                  if (intValue < minRange || intValue > maxRange) {
                    return Promise.reject(
                      `${label} must be between ${minRange} and ${maxRange}`
                    );
                  }
                }

                return Promise.resolve();
              }
              // we have a dropdown
              if (!value) {
                return Promise.reject(`${label} selection required`);
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        {profileField === 'us_coat_length' ? (
          <CoatLengthSelect />
        ) : options ? (
          <StyledSelect
            options={
              options?.map((option) => ({
                label: option,
                value: option,
              })) || []
            }
            placeholder='Select Size'
          />
        ) : (
          <StyledInput placeholder='Type here' />
        )}
      </StyledFormItem>
    </StyledGapContainer>
  );
};

const CoatLengthSelect = (props) => {
  const wholeValue = props?.value
    ? US_COAT_LENGTH_VALUE_TO_OPTION[props?.value]
    : null;
  const selectValue = wholeValue
    ? {
        label: wholeValue?.shortLabel,
        key: wholeValue?.value,
        value: wholeValue?.value,
      }
    : null;

  return (
    <StyledSelect
      {...props}
      options={US_COAT_LENGTH_OPTIONS.map(({ value, longLabel }) => ({
        label: longLabel,
        value,
        key: value,
      }))}
      placeholder='Select Size'
      value={selectValue}
    />
  );
};
